<!-- One line in the bag: the bag's front, name, roaster · size, the line price and a
     quantity stepper. At quantity 1 the stepper's minus becomes a trash can, and
     a swipe left also removes; both go through the parent's `remove`, which
     offers undo. Owned by the Bag-tab agent. -->
<template>
  <IonItemSliding class="bag-line" :class="{ 'bag-line--lifted': lifted }">
    <IonItem lines="none" class="bag-line__item">
      <div slot="start" class="bag-line__thumb">
        <BagFront :coffee="line.coffee" :size="line.grams || 250" variant="thumb" />
      </div>

      <div class="bag-line__body">
        <div class="bag-line__top">
          <h2 class="bag-line__name">{{ displayName(line.coffee.name) }}</h2>
          <span class="bag-line__price">{{ aud(line.price * line.qty) }}</span>
        </div>
        <div class="bag-line__bottom">
          <p class="bag-line__meta">{{ line.coffee.roaster }} · {{ line.size }}</p>

          <div class="stepper" role="group" :aria-label="`Quantity of ${line.coffee.name}`">
            <button
              v-if="line.qty <= 1"
              type="button"
              class="stepper__btn stepper__btn--trash"
              :aria-label="`Remove ${line.coffee.name} from your bag`"
              @click="$emit('remove')"
            >
              <IonIcon :icon="trashOutline" aria-hidden="true" />
            </button>
            <button
              v-else
              type="button"
              class="stepper__btn"
              :aria-label="`One fewer ${line.coffee.name}`"
              @click="step(-1)"
            >
              <IonIcon :icon="removeOutline" aria-hidden="true" />
            </button>
            <output class="stepper__qty" aria-live="polite">{{ line.qty }}</output>
            <button
              type="button"
              class="stepper__btn"
              :disabled="line.qty >= 20"
              :aria-label="`One more ${line.coffee.name}`"
              @click="step(1)"
            >
              <IonIcon :icon="addOutline" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </IonItem>

    <IonItemOptions side="end" @ionSwipe="$emit('remove')">
      <IonItemOption class="bag-line__remove" expandable @click="$emit('remove')">Remove</IonItemOption>
    </IonItemOptions>
  </IonItemSliding>
</template>

<script setup>
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption, IonIcon } from '@ionic/vue';
import { addOutline, removeOutline, trashOutline } from 'ionicons/icons';
import { setQty } from '../store.js';
import { haptic } from '../core/util.js';
import BagFront from './BagFront.vue';
import { displayName } from './CoffeeLabel.vue';

const props = defineProps({
  /** A `bagLines` entry: BagItem + { coffee }. */
  line: { type: Object, required: true },
  /** Its pouch is lifted in the shopping bag. */
  lifted: { type: Boolean, default: false },
});
defineEmits(['remove']);

const aud = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);


function step(d) {
  setQty(props.line.id, props.line.qty + d);
  haptic('tick');
}
</script>

<style scoped>
.bag-line__item {
  --padding-start: var(--sp-3);
  --inner-padding-end: var(--sp-3);
  --padding-top: var(--sp-2);
  --padding-bottom: var(--sp-2);
  --min-height: calc(var(--sp-8) + var(--sp-2));   /* ~72pt, the spec's row */
  /* A sliding row must be opaque so it travels over its action. */
  --background: var(--dock, #FAF7F1);
  --background-hover: var(--dock, #FAF7F1);
}

.bag-line--lifted .bag-line__item { --background: color-mix(in srgb, var(--mustard, #F2C230) 16%, var(--dock, #FAF7F1)); }
.bag-line--lifted .bag-line__thumb { box-shadow: inset 0 -2px 0 var(--mustard, #F2C230); }

/* The bag's front standing in a small paper bay. */
.bag-line__thumb {
  display: grid;
  place-items: end center;
  flex: none;
  width: calc(var(--sp-7) + var(--sp-1));
  height: calc(var(--sp-7) + var(--sp-2));
  margin: 0 var(--sp-3) 0 0;
  padding: var(--sp-1) var(--sp-1) 2px;
  border-radius: var(--radius-sm);
  background: var(--paper, #F4EFE6);
  transition: box-shadow var(--dur) var(--ease);
}
.bag-line__thumb :deep(.bag-front) { height: 100%; width: auto; max-width: 100%; }

.bag-line__body { flex: 1; min-width: 0; }
.bag-line__top,
.bag-line__bottom { display: flex; align-items: center; gap: var(--sp-3); }
.bag-line__top { align-items: baseline; }
.bag-line__bottom { margin-top: var(--sp-1); }

.bag-line__name {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: var(--ink, #1F2A22);
  font-size: calc(var(--text-md) * 0.9375);
  font-weight: 800;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.bag-line__price {
  flex: none;
  color: var(--ink, #1F2A22);
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-size: var(--text-sm);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

.bag-line__meta {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: var(--muted, #5E6B60);
  font-size: var(--text-sm);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stepper {
  flex: none;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: var(--sage, #E6EDE4);
}

/* 36pt drawn, 44pt to the finger: the hit area overhangs the pill. */
.stepper__btn {
  position: relative;
  display: grid;
  place-items: center;
  width: calc(var(--sp-6) + var(--sp-1));
  height: calc(var(--sp-6) + var(--sp-1));
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--ink, #1F2A22);
  font-size: var(--text-md);
  cursor: pointer;
  transition: transform 120ms var(--ease), opacity var(--dur);
}
.stepper__btn::before { content: ''; position: absolute; inset: calc(var(--sp-1) * -1); }
.stepper__btn--trash { color: var(--muted, #5E6B60); }
.stepper__btn:active:not(:disabled) { transform: scale(0.9); }
.stepper__btn:disabled { opacity: 0.3; cursor: default; }
.stepper__btn:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: -2px; }

.stepper__qty {
  min-width: 1.6em;
  color: var(--ink, #1F2A22);
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-size: var(--text-sm);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.bag-line__remove {
  --background: var(--ink, #1F2A22);
  --color: var(--dock, #FAF7F1);
  font-weight: 700;
  text-transform: none;
}
@media (prefers-reduced-motion: reduce) { .stepper__btn, .bag-line__thumb { transition: none; } }
</style>
